import sql from "sql-template-tag";
import { type ActorRefFromLogic, createActor, waitFor } from "xstate";

import { getDb } from "~/data/sql";
import { createRepo, getGiteaApiUrlWithoutProtocol } from "~/utils/gitea";

import { type AuthenticationMachine, authnStateNotUnknown } from "../authentication";
import { createQueryMachine } from "../query.machine";

export const createRemoteRepo = async ({
	input,
}: {
	input: {
		workspaceName: string;
		documentId: string;
		authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
		skip: boolean;
	};
}) => {
	const { workspaceName, documentId, skip } = input;
	if (skip) {
		return;
	}

	const actor = createActor(
		createQueryMachine({
			shouldEnd: true,
			queryKey: `create-remote-repo-${workspaceName}-${documentId}`,
			queryFn: async () => {
				const authSnap = await waitFor(input.authenticationActorRef, authnStateNotUnknown);
				const token = await authSnap.context.getAccessTokenSilently();
				const upstreamWorkspace =
					workspaceName ||
					(authSnap.context.authnStatus === "authenticated" &&
						authSnap.context.user.nickname) ||
					"";

				if (!token) {
					throw new Error("Not logged in");
				}

				return await createRepo(token, upstreamWorkspace, documentId);
			},
			persistData: async () => {
				const authSnap = await waitFor(input.authenticationActorRef, authnStateNotUnknown);
				const upstreamWorkspace =
					workspaceName ||
					(authSnap.context.authnStatus === "authenticated" &&
						authSnap.context.user.nickname) ||
					"";

				const host = getGiteaApiUrlWithoutProtocol();
				const slug = upstreamWorkspace;
				const name = upstreamWorkspace;
				const id = `${host}/${slug}`;

				const db = await getDb();

				const query = sql`
						INSERT INTO workspaces (id, host, slug, name) VALUES (${id}, ${host}, ${slug}, ${name})
						ON CONFLICT (id) DO UPDATE SET name = ${name}
					`;
				await db?.execute(query.sql, query.values);

				const query2 = sql`
						INSERT INTO documents_workspaces (documentId, workspaceId) VALUES (${documentId}, ${workspaceName})
						ON CONFLICT (documentId, workspaceId) DO NOTHING
					`;
				await db?.execute(query2.sql, query2.values);
			},
		})
	);
	actor.start();
	return (await waitFor(actor, (state) => state.status === "done")).output;
};
